import { flattenDeep } from 'lodash'

import {
  addToCartDataLayer,
  removeProductDataLayer,
  addServiceToCartDataLayer,
  removeServiceFromCartDataLayer,
  cartCepToDataLayer,
  checkoutCartPurchaseToDataLayer,
  pushDataObject
} from 'scripts/utils/data-layer'

import formatPriceToBRL from 'reactUtils/formatPriceToBRL'

const getModalitiesDimension = modalities => modalities.reduce((acc, { cost, type }) => {
  const formattedCost = formatPriceToBRL(cost).replace(/\s/g, '')
  const modalityDimension = `${type}_${formattedCost}`

  if (acc === '') return modalityDimension

  return `${acc} - ${modalityDimension}`
}, '')

const sanitizeRegionName = regionName => {
  let name = null

  if (typeof regionName === 'string') {
    name = regionName.split(' -')[0]
  }

  return name
}

const storeDimension = (shopName, type, dimension) => {
  if (shopName && type === 'delivery' && dimension === 15) {
    return shopName
  }

  if (shopName && type === 'pickUpInStore' && dimension === 9) {
    return shopName
  }

  return ''
}

const buildProductDatalayer = (product, { type, deadlineMessage }) => {
  const { discount } = product.price
  const hasDiscount = discount > 0

  return {
    name: product.name,
    id: product.productId,
    brand: product.brand || '',
    category: product.categoryTree || '',
    quantity: product.quantity,
    price: product.price.to,
    dimension8: type || '',
    dimension9: storeDimension(product.shopName, type, 9),
    dimension15: storeDimension(product.shopName, type, 15),
    dimension16: product.deliveryForecast || '',
    dimension17: deadlineMessage,
    dimension21: hasDiscount ? 'com-desconto' : 'sem-desconto',
    dimension22: hasDiscount ? discount : ''
  }
}

const getItemsByShipment = (shipment, { type }) => {
  const products = shipment.items
  const deadlineMessage = shipment.modalities.find(
    modality => modality.type === 'standard' || modality.type === 'express'
  )?.deadlineMessage || ''

  return products.map(product => buildProductDatalayer(product, { type, deadlineMessage }))
}

const getProductsDatalayer = (shippings) => {
  const productsDatalayer = shippings.map(shipping => {
    const type = shipping.type

    return shipping.shipments.map(shipment => getItemsByShipment(shipment, { type }))
  })

  return flattenDeep(productsDatalayer)
}

const getServicesDatalayer = (services = []) => {
  if (!services.length) {
    return []
  }

  return services.map(service => (
    {
      name: service.name,
      id: service.serviceId,
      brand: 'Serviço',
      category: service.serviceBankName,
      quantity: service.quantity,
      price: service.price.to,
      dimension8: 'delivery',
      dimension9: 'Leroy Merlin',
      dimension15: 'Leroy Merlin',
      dimension16: '',
      dimension17: '3 dias úteis após a entrega ou retirada do produto',
      dimension21: 'sem-desconto',
      dimension22: '',
      dimension31: service.relatedProduct.name || ''
    }
  ))
}

const getCheckoutDatalayer = (regionId, shippings, services) => {
  const productsDatalayer = getProductsDatalayer(shippings)
  const servicesDatalayer = getServicesDatalayer(services)

  return checkoutCartPurchaseToDataLayer({
    regionId,
    products: servicesDatalayer ? productsDatalayer.concat(servicesDatalayer) : productsDatalayer
  })
}

export const cartCheckout = ({ shippings }, regionId, services) => {
  const dataObject = getCheckoutDatalayer(regionId, shippings, services)

  pushDataObject(dataObject)
}

export const addProduct = ({
  brand,
  categoryTree: category,
  id,
  name,
  price,
  type,
  quantity,
  shopName,
  regionId
}) => {
  const dataObject = addToCartDataLayer({
    regionId,
    productName: name,
    productId: id,
    price: price?.to,
    productBrand: brand,
    category,
    quantity,
    dimension8: type || null,
    dimension9: storeDimension(shopName, type, 9),
    dimension15: storeDimension(shopName, type, 15)
  })

  pushDataObject(dataObject)
}

export const removeProduct = product => {
  const dataObject = removeProductDataLayer(product)

  pushDataObject(dataObject)
}

export const addService = service => {
  const dataObject = addServiceToCartDataLayer(service)

  pushDataObject(dataObject)
}

export const removeService = service => {
  const dataObject = removeServiceFromCartDataLayer(service)

  pushDataObject(dataObject)
}

export const freightCostsCart = shippings => {
  const shippingsReducer = (shippingsAccumulator, { shipments }) => {
    const shipmentReduce = (shipmentAccumulator, { items, modalities }) => {
      const itemsData = items.map(item => ({
        name: item.name,
        dimension28: getModalitiesDimension(modalities)
      }))

      return [...shipmentAccumulator, ...itemsData]
    }

    const data = shipments.reduce(shipmentReduce, [])

    return [...shippingsAccumulator, ...data]
  }

  const products = shippings.reduce(shippingsReducer, [])
  const dataObject = cartCepToDataLayer(products)

  pushDataObject(dataObject)
}

export default {
  cartCheckout,
  addProduct,
  removeProduct,
  addService,
  removeService,
  freightCostsCart
}
